import { FC, ReactNode } from "react";
import classNames from "classnames";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Semibold, Body } from "capitalrx-components-2.0/lib/typography";
import { BaseProps } from "../../BaseProps";
import styles from "./styles.module.scss";

/**
 * The `InputAlignmentContainer` properties.
 */
export interface InputAlignmentContainerProps extends BaseProps {
  /**
   * The set of child components.
   */
  readonly children?: ReactNode;
}

/**
 * A layout component that aligns children to form inputs by padding the top of the container
 * with the same metrics as an input label.
 *
 * This component is purely presentational and does not add any semantic meaning or
 * interactive behavior. It maintains accessibility by preserving the natural document
 * structure and not interfering with assistive technology.
 *
 * `import { InputAlignmentContainer } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>Example use case:</strong>
 *
 * ```typescript
 * <InputAlignmentContainer>
 *   <Button>Aligned Button</Button>
 * </InputAlignmentContainer>
 * ```
 */
export const InputAlignmentContainer: FC<InputAlignmentContainerProps> = (props) => {
  const { children, className, style, testId } = props;

  return (
    <Stack
      className={classNames(
        styles.InputAlignmentContainer,
        "input-alignment-container",
        className,
      )}
      style={style}
      testId={testId}
      spacing="mini"
      alignment="span"
    >
      <Body inline size="mini" className="alignment-spacer">
        <Semibold>
          <span aria-hidden="true">&nbsp;</span>
        </Semibold>
      </Body>
      <Stack className="content" alignment="span">
        <Stack.Spacer />
        {children}
        <Stack.Spacer />
      </Stack>
    </Stack>
  );
};
